/**
 * Mounted featured rows and their teardown callbacks.
 *
 * A soft navigation replaces #MainContent wholesale, so every row mounted on
 * the previous page is detached. Its window 'resize' listener survives on the
 * long-lived window and its closure keeps the detached scroller/controls/items
 * alive, leaking once per soft navigation. The next scan tears down the rows
 * that are no longer connected before mounting the new page's rows.
 */
const featuredRowTeardowns = [];

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function initFeaturedRow(controls) {
  if (controls.dataset.navInited === 'true') return;
  controls.dataset.navInited = 'true';

  const section = controls.closest('.section');
  const scroller = section.querySelector('.featured-row-scroller');
  const prev = controls.querySelector('.featured-row-nav--prev');
  const next = controls.querySelector('.featured-row-nav--next');
  const currentEl = controls.querySelector('[data-featured-row-current]');
  const totalEl = controls.querySelector('[data-featured-row-total]');
  if (!scroller || !prev || !next) return;

  const items = scroller.querySelectorAll('.featured-row-item');

  function getCardStep() {
    if (!items.length) return 300;
    return items[0].offsetWidth + parseFloat(getComputedStyle(scroller).gap || 20);
  }

  function getVisibleCount() {
    return Math.floor(scroller.offsetWidth / getCardStep());
  }

  function getScrollCount() {
    return Math.max(1, getVisibleCount() - 1);
  }

  function updateState() {
    const step = getCardStep();
    const scrollCount = getScrollCount();
    const remaining = items.length - getVisibleCount();
    const totalPages = Math.max(1, Math.ceil(remaining / scrollCount) + 1);

    if (totalPages <= 1) {
      controls.style.display = 'none';
      return;
    }

    controls.style.display = '';
    let currentPage = Math.round(scroller.scrollLeft / (step * scrollCount)) + 1;
    currentPage = Math.min(currentPage, totalPages);

    prev.disabled = scroller.scrollLeft <= 0;
    next.disabled = scroller.scrollLeft + scroller.offsetWidth >= scroller.scrollWidth - 1;

    if (currentEl) currentEl.textContent = currentPage;
    if (totalEl) totalEl.textContent = totalPages;
  }

  prev.addEventListener('click', () => {
    const step = getCardStep() * getScrollCount();
    scroller.scrollBy({ left: -step, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  });

  next.addEventListener('click', () => {
    const step = getCardStep() * getScrollCount();
    scroller.scrollBy({ left: step, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  });

  let scrollFrame = 0;
  function onScroll() {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      updateState();
    });
  }

  let resizeTimer = 0;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(updateState, 200);
  }

  scroller.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);

  featuredRowTeardowns.push({
    controls,
    teardown() {
      window.removeEventListener('resize', onResize);
      scroller.removeEventListener('scroll', onScroll);
      clearTimeout(resizeTimer);
    },
  });

  updateState();
}

function initFeaturedRows() {
  // Tear down rows detached by the previous swap before mounting this page's
  // rows. A row that survived the navigation is still connected, so it is
  // left untouched and keeps its listeners and its navInited guard.
  for (let i = featuredRowTeardowns.length - 1; i >= 0; i--) {
    const entry = featuredRowTeardowns[i];
    if (entry.controls.isConnected) continue;
    entry.teardown();
    featuredRowTeardowns.splice(i, 1);
  }

  document.querySelectorAll('.featured-row-controls').forEach((controls) => {
    if (controls.dataset.navInited === 'true') return;
    initFeaturedRow(controls);
  });
}

initFeaturedRows();
document.addEventListener('site:navigation:end', initFeaturedRows);
